import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToSection } from "./scroll";
import { useLang } from "@/i18n";
import { useTheme } from "@/theme";

function LangToggle({ overDark = false }) {
  const { lang, setLang } = useLang();
  const active = overDark ? "text-[#C2A675]" : "text-gold";
  const idle = overDark
    ? "text-[#F9F8F3] opacity-50 hover:opacity-100"
    : "text-bone opacity-50 hover:opacity-100";
  return (
    <div
      className="flex items-center gap-1 font-mono text-xs"
      data-testid="language-toggle"
    >
      {["fr", "en"].map((l, i) => (
        <span key={l} className="flex items-center">
          {i > 0 && <span className="opacity-40 mx-1">·</span>}
          <button
            data-testid={`language-toggle-${l}`}
            onClick={() => setLang(l)}
            className={`uppercase tracking-[0.2em] px-1 py-1 transition-colors duration-300 ${
              lang === l ? active : idle
            }`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
}

export default function Nav() {
  const { t } = useLang();
  const { theme, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "villa", label: t.nav.villa },
    { id: "bungalow", label: t.nav.bungalow },
    { id: "marina", label: t.nav.marina },
    { id: "bassins", label: t.nav.bassins },
    { id: "activites", label: t.nav.activites },
    { id: "situation", label: t.nav.situation },
    { id: "galerie", label: t.nav.gallery },
    { id: "avis", label: t.nav.reviews },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToSection(id), open ? 250 : 0);
  };

  const linkCls = scrolled
    ? "text-mist hover:text-gold"
    : "text-[#D0D7D1] hover:text-[#E6CE9F]";

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "glass" : "bg-transparent"
        }`}
      >
        <nav className="mx-auto max-w-[1500px] px-5 sm:px-10 h-[96px] flex items-center justify-between">
          <button
            data-testid="nav-logo"
            onClick={() => go("accueil")}
            className="flex items-center transition-opacity duration-300 hover:opacity-80"
          >
            <Logo size={78} />
          </button>

          <div className="hidden lg:flex items-center gap-6">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`font-mono text-[0.65rem] uppercase tracking-[0.25em] transition-colors duration-300 ${linkCls}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-5">
            <LangToggle overDark={!scrolled} />
            <button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              aria-label="Basculer jour / nuit"
              className={`transition-colors duration-300 ${
                scrolled ? "text-gold hover:text-goldlight" : "text-[#C2A675] hover:text-[#E6CE9F]"
              }`}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              data-testid="nav-book-button"
              onClick={() => go("reservations")}
              className="hidden sm:inline-flex items-center gap-2 border border-gold/50 text-gold hover:bg-gold hover:text-night px-5 py-2.5 font-mono text-[0.65rem] uppercase tracking-[0.22em] transition-all duration-300"
            >
              {t.nav.cta}
            </button>
            <button
              data-testid="nav-menu-toggle"
              className={`lg:hidden font-mono text-[0.65rem] uppercase tracking-[0.25em] ${
                scrolled ? "text-bone" : "text-[#F9F8F3]"
              }`}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
            >
              {open ? t.nav.close : t.nav.menu}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-night/95 backdrop-blur-xl flex flex-col items-center justify-center gap-7"
          >
            {links.map((l, i) => (
              <motion.button
                key={l.id}
                data-testid={`mobile-menu-link-${l.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.07 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => go(l.id)}
                className="font-display text-3xl sm:text-4xl font-light text-bone hover:text-gold transition-colors"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              data-testid="mobile-menu-book"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onClick={() => go("reservations")}
              className="mt-4 border border-gold text-gold px-8 py-3 font-mono text-xs uppercase tracking-[0.25em]"
            >
              {t.nav.cta}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
